import LexicalEditor, { type LexicalEditorHandle } from '@/components/Editor/LexicalEditor'
import { FileAttachment } from '@/utils/fetchers/attachment'
import { COMMAND_PRIORITY_NORMAL, PASTE_COMMAND } from 'lexical'
import { isEmpty } from 'lodash-es'
import {
	type ClipboardEvent,
	type CompositionEvent,
	type Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react'
import { type ControllerRenderProps } from 'react-hook-form'
import toast from 'react-hot-toast'
import { filterTransferItems } from '../Attachment/utils'
import styles from './ContentEditor.module.css'
import type { PostFormValue } from './PostForm/schemas'

export interface ContentEditorProps extends Omit<
	ControllerRenderProps<PostFormValue, 'content'>,
	'ref'
> {
	/**
	 *
	 * @default false
	 */
	autoFocus?: boolean

	onPasteAttachments?: (attachments: FileAttachment[]) => void
	onSubmit?: () => void
	ref?: Ref<{ editor: LexicalEditorHandle | null }>
}

export default function ContentEditor({
	onChange,
	onBlur,
	value,
	disabled,
	autoFocus = false,
	onPasteAttachments,
	onSubmit,
	ref,
	...props
}: ContentEditorProps) {
	const editorHandleRef = useRef<LexicalEditorHandle>(null)
	const isCompositingRef = useRef<boolean>(false)

	const handleEditorValueUpdate = useCallback(
		(newValue: string) => {
			if (isCompositingRef.current) {
				return
			}
			onChange(newValue)
		},
		[onChange]
	)

	const handlePaste = useCallback(
		(e: ClipboardEvent<HTMLElement>) => {
			const files = Array.from(e.clipboardData.files)
			const dataTransferItems = Array.from(e.clipboardData.items)

			if (isEmpty(files) || isEmpty(dataTransferItems)) {
				return false
			}
			e.preventDefault()

			const { images, videos } = filterTransferItems(dataTransferItems)

			// Validate that images and videos cannot be dropped simultaneously
			if (images.length > 0 && videos.length > 0) {
				// Show toast notification to inform user
				toast.error('不能同时添加图片和视频')
				return false
			}

			onPasteAttachments?.(images.concat(videos) as FileAttachment[])

			return true
		},
		[onPasteAttachments]
	)

	useImperativeHandle(
		ref,
		() => ({
			get editor() {
				return editorHandleRef.current
			},
		}),
		[]
	)

	useEffect(() => {
		const editor = editorHandleRef.current?.editor

		if (!editor) {
			return
		}
		return editor.registerCommand(
			PASTE_COMMAND,
			(payload) => handlePaste(payload as unknown as ClipboardEvent<HTMLElement>),
			COMMAND_PRIORITY_NORMAL
		)
	}, [handlePaste])

	return (
		<LexicalEditor
			spellCheck={false}
			value={value}
			ref={editorHandleRef}
			placeholder={<div>分享你的想法...</div>}
			onChange={handleEditorValueUpdate}
			onSubmit={onSubmit}
			data-autofocus={autoFocus}
			onCompositionStart={() => {
				isCompositingRef.current = true
			}}
			onCompositionEnd={(e: CompositionEvent) => {
				isCompositingRef.current = false
				editorHandleRef.current?.getText().then(onChange)
			}}
			className={`content-editor ${styles.editor}`}
		/>
	)
}
